/* eslint-disable @typescript-eslint/array-type */
import {SearchOutlined} from '@ant-design/icons';
import type {InputRef} from 'antd';
import {Input, Table as TableAnt} from 'antd';
import {ColumnType, ColumnsType} from 'antd/es/table';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';

import PilComp from '../../../components/DisplayComponent/PilComp';
import Loader from '../../../components/Loader/Loader';
import {Title} from '../../../components/Table/Constants/Common';
import {defaultCol} from '../../../components/Table/Interface/ITable';
import {isValueOrNot} from '../../../components/Table/Utils/filterReturn';
import TableTitleHeader from '../../../components/Table/Utils/TableTitleHeader';
import type {PCFRecord, PCFStatus} from '../../../services/interface/pcfTypes';
import {
  deletePCFRecord,
  listPCFRecords
} from '../../../services/modules/PCF/pcfAPI';
import useCompanyStore from '../../../store/companyStore/companyDetailsStore';
import usePCFStore from '../../../store/pcfStore/pcfStore';
import showMessage from '../../../utils/showMessage';
import {buildDefaultRecordData} from '../constants/pcfDefaultValues';
import {GoalAndScopeLabels} from '../PCFKeyLabels';

import {getPCFStepPath} from './pcfRoutes';
import PCFStudyActions from './PCFStudyActions';

const STATUS_LABELS: Record<PCFStatus, string> = {
  draft: 'Draft',
  approved: 'Approved'
};

const PCFStudiesTable: React.FC = () => {
  const navigate = useNavigate();
  const companyId = useCompanyStore((state) => state.companyId);
  const resetPCFStore = usePCFStore((state) => state.resetPCFStore);

  const [dataSource, setDataSource] = useState<PCFRecord[]>([]);
  const [refresh, setRefresh] = useState(false);
  const [isRefresh, setIsRefresh] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [shouldAdd, setShouldAdd] = useState(false);

  const fetchRecords = useCallback(() => {
    if (!companyId) {
      setDataSource([]);
      setIsLoading(false);
      setIsRefresh(false);

      return;
    }

    setIsRefresh(true);
    listPCFRecords(companyId)
      .then((records) => {
        setDataSource(records);
      })
      .catch(() => {
        setDataSource([]);
        showMessage({
          status: 'error',
          message: 'Failed to load PCF studies. Please try again.'
        });
      })
      .finally(() => {
        setIsLoading(false);
        setIsRefresh(false);
        setRefresh(false);
      });
  }, [companyId]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  useEffect(() => {
    if (refresh) {
      fetchRecords();
    }
  }, [refresh, fetchRecords]);

  useEffect(() => {
    if (shouldAdd) {
      resetPCFStore(buildDefaultRecordData());
      navigate(getPCFStepPath('goalAndScope'));
      setShouldAdd(false);
    }
  }, [shouldAdd, navigate, resetPCFStore]);

  const handleEdit = (record: PCFRecord) => {
    const recordId = record._id;
    if (!recordId) {
      return;
    }

    navigate(getPCFStepPath('goalAndScope', recordId));
  };

  const handleDelete = async (id: string) => {
    try {
      const result = await deletePCFRecord(id);
      if (result.status === 'error') {
        showMessage({
          status: 'error',
          message: result.message ?? 'Failed to delete study.'
        });

        return;
      }

      showMessage({status: 'success', message: 'Study deleted successfully.'});
      setRefresh(true);
    } catch {
      showMessage({
        status: 'error',
        message: 'Failed to delete study. Please try again.'
      });
    }
  };

  const searchInput = useRef<InputRef>(null);

  const SearchFilter = (
    title: string,
    dataIndex:
      | keyof PCFRecord
      | 'studyName'
      | 'productName'
      | 'studyDescription'
  ): defaultCol & ColumnType<PCFRecord> => {
    return {
      filterDropdown: ({selectedKeys, setSelectedKeys, confirm}) => (
        <div
          style={{padding: 8}}
          onKeyDown={(e) => {
            e.stopPropagation();
          }}
        >
          <Input
            ref={searchInput}
            value={selectedKeys[0]}
            placeholder={`Search ${title}`}
            onChange={(e) => {
              setSelectedKeys(e.target.value ? [e.target.value] : []);
            }}
            onPressEnter={() => {
              confirm();
            }}
            onBlur={() => {
              confirm();
            }}
            allowClear
          />
        </div>
      ),
      filterIcon: <SearchOutlined />,
      onFilter: (value, record) => {
        let fieldValue = '';
        if (dataIndex === 'studyName') {
          fieldValue = record.data?.goalAndScope?.studyName ?? '';
        } else if (dataIndex === 'productName') {
          fieldValue = record.data?.goalAndScope?.productName ?? '';
        } else if (dataIndex === 'studyDescription') {
          fieldValue = record.data?.goalAndScope?.studyDescription ?? '';
        } else {
          fieldValue = String(record[dataIndex as keyof PCFRecord] ?? '');
        }

        return fieldValue
          .toString()
          .toLowerCase()
          .includes(value.toString().toLowerCase());
      },
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      }
    };
  };

  const columns: (defaultCol & ColumnsType<PCFRecord>[0])[] = [
    {
      title: GoalAndScopeLabels.STUDY_NAME,
      dataIndex: 'studyName',
      key: 'studyName',
      ...SearchFilter(GoalAndScopeLabels.STUDY_NAME, 'studyName'),
      render: (_value, record) =>
        isValueOrNot(record.data?.goalAndScope?.studyName ?? '')
    },
    {
      title: 'Product Name',
      dataIndex: 'productName',
      key: 'productName',
      ...SearchFilter('Product Name', 'productName'),
      render: (_value, record) =>
        isValueOrNot(record.data?.goalAndScope?.productName ?? '')
    },
    {
      title: 'Study Description',
      dataIndex: 'studyDescription',
      key: 'studyDescription',
      ...SearchFilter('Study Description', 'studyDescription'),
      render: (_value, record) =>
        isValueOrNot(record.data?.goalAndScope?.studyDescription ?? '')
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      filters: [
        {text: 'Draft', value: 'draft'},
        {text: 'Approved', value: 'approved'}
      ],
      onFilter: (value, record) => record.status === value,
      render: (value: PCFStatus) => (
        <PilComp
          color={value === 'approved' ? '#BFE6AC' : '#8BA7B8'}
          value={STATUS_LABELS[value] ?? value}
        />
      )
    },
    {
      title: Title.actionTitle,
      dataIndex: 'operation',
      key: 'operation',
      fixed: 'right',
      width: '200px',
      render: (_value, record) =>
        dataSource.length >= 1 ? (
          <PCFStudyActions
            record={record}
            onEdit={handleEdit}
            onDelete={(id) => {
              void handleDelete(id);
            }}
          />
        ) : null
    }
  ];

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div>
      <TableTitleHeader
        title=""
        filter={{facilityId: '', selectedYear: ''}}
        addButton={{
          setShowModel: setShouldAdd,
          setIsEdit: () => {},
          buttonTitle: 'Add new PCF'
        }}
        sampleDownload={{href: ''}}
        jobs={{setShowJobs: null, uploadType: ''}}
        deleteButton={{selectedRowKeys: [], handleDelete: () => {}}}
        refresh={{setRefresh}}
        showAddOnly={true}
        isTable={false}
        shouldDisable={false}
      />
      <TableAnt
        rowKey="_id"
        dataSource={dataSource}
        columns={columns as ColumnsType<PCFRecord>}
        scroll={{x: 'auto'}}
        loading={{
          indicator: <Loader />,
          spinning: isRefresh
        }}
        pagination={false}
      />
    </div>
  );
};

export default PCFStudiesTable;
